<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { slide, fade } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';

  type Option = { value: string; label: string; weight?: number };
  type Question = {
    id: string;
    type: 'single' | 'multi' | 'text' | 'matrix';
    label: string;
    description?: string;
    options?: Option[];
    placeholder?: string;
    conditional_on?: string;
    conditional_values?: string[];
    rows?: string[];
    cols?: string[];
    row_keys?: string[];
    col_keys?: string[];
  };
  type Survey = { slug: string; title: string; description: string | null; questions: Question[];
                  published: boolean; closed: boolean };
  type Prefill = {
    respondent_badge: string; respondent_name: string | null;
    threads_username: string; email: string | null;
    answers: Record<string, string | string[]>;
  } | null;

  let { survey, prefill }: { survey: Survey; prefill: Prefill } = $props();

  let name = $state(prefill?.respondent_name ?? '');
  let threads = $state(prefill?.threads_username ?? '');
  let email = $state(prefill?.email ?? '');
  let badge = $state(prefill?.respondent_badge ?? '');
  let answers: Record<string, string | string[]> = $state({ ...(prefill?.answers ?? {}) });
  let showBadgeInput = $state(false);
  let phone = $state((prefill?.answers?.phone as string) ?? '');

  function toggleMulti(qid: string, value: string) {
    const current = Array.isArray(answers[qid]) ? (answers[qid] as string[]) : [];
    answers[qid] = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
  }

  function isMatrixChecked(qid: string, rowKey: string, colKey: string): boolean {
    const val = `${rowKey}_${colKey}`;
    return Array.isArray(answers[qid]) && (answers[qid] as string[]).includes(val);
  }

  function isAnswered(q: Question): boolean {
    const a = answers[q.id];
    if (a === undefined || a === null) return false;
    if (typeof a === 'string') return a.trim().length > 0;
    if (Array.isArray(a)) return a.length > 0;
    return false;
  }

  // 점진적 노출: 답한 질문까지 + 그 다음 하나만 보임 (집중 UX)
  let revealUntil = $derived.by(() => {
    if (!prefill) {
      for (let i = 0; i < survey.questions.length; i++) {
        const q = survey.questions[i];
        if (!shouldShowQuestion(q)) continue;
        if (!isAnswered(q)) return i;
      }
    }
    return survey.questions.length - 1;
  });

  function shouldShowQuestion(q: Question): boolean {
    if (!q.conditional_on || !q.conditional_values) return true;
    const parentAnswer = answers[q.conditional_on];
    if (typeof parentAnswer === 'string') return q.conditional_values.includes(parentAnswer);
    if (Array.isArray(parentAnswer)) return parentAnswer.some(v => q.conditional_values!.includes(v));
    return false;
  }

  function submit(e: Event) {
    e.preventDefault();
    if (!phone.trim()) { alert('휴대폰 번호를 입력해주세요'); return; }

    const answersWithPhone = { ...answers };
    answersWithPhone.phone = phone.trim();

    router.post(`/vibe_study/survey/${survey.slug}/submit`, {
      response: {
        respondent_name: name,
        threads_username: threads.replace(/^@/, ''),
        email: email || null,
        respondent_badge: badge || null,
        answers: answersWithPhone
      }
    });
  }
</script>

<svelte:head>
  <title>{survey.title}</title>
</svelte:head>

<div class="ios-shell">
  <main class="ios-container">
    <header class="ios-header">
      <p class="ios-slogan">We are so early</p>
      <h1 class="ios-title">{survey.title}</h1>
      {#if survey.description}
        <p class="ios-subtitle">{survey.description}</p>
      {/if}
    </header>

    {#if survey.closed}
      <div class="ios-card ios-card--welcome">
        <div class="ios-welcome-icon">🎉</div>
        <h2 class="ios-welcome-title">환영합니다</h2>
        <p class="ios-welcome-body">
          설문에 참여해 주셔서 감사합니다.<br/>
          AX 문화를 함께 만들어가는 커뮤니티의 시작에 오신 것을 환영합니다.
        </p>
        <p class="ios-welcome-sub">곧 연락드리겠습니다 ☀️</p>
      </div>
    {:else if !survey.published}
      <div class="ios-card ios-card--notice">아직 공개되지 않은 설문입니다.</div>
    {:else}
      <form onsubmit={submit} class="ios-form">
        <!-- 기본 정보 그룹 -->
        <section class="ios-section">
          <h2 class="ios-section-title">기본 정보</h2>
          <p class="ios-basic-hint">휴대폰 번호는 사전 안내 및 연락을 위해 필수입니다.</p>
          <div class="ios-group">
            <label class="ios-row">
              <span class="ios-row-label">이름 또는 닉네임</span>
              <input bind:value={name} class="ios-input" placeholder="선택사항" />
            </label>
            <label class="ios-row">
              <span class="ios-row-label">휴대폰 번호 <span class="ios-required">*</span></span>
              <input type="tel" bind:value={phone} inputmode="numeric"
                     class="ios-input" placeholder="010-0000-0000" required />
            </label>
            <label class="ios-row">
              <span class="ios-row-label">Threads</span>
              <input bind:value={threads} placeholder="@seungha__n (선택)" class="ios-input" />
            </label>
            <label class="ios-row">
              <span class="ios-row-label">이메일</span>
              <input type="email" bind:value={email} class="ios-input" placeholder="선택사항" />
            </label>
          </div>

          {#if !showBadgeInput}
            <button type="button" onclick={() => showBadgeInput = true} class="ios-link-btn">
              이전 응답 수정 (응답 코드 보유)
            </button>
          {:else}
            <div class="ios-group">
              <label class="ios-row">
                <span class="ios-row-label">응답 코드</span>
                <input bind:value={badge} placeholder="VBS-xxxx-xxxx-xxxx"
                       class="ios-input ios-input--mono" />
              </label>
            </div>
          {/if}
        </section>

        <!-- 질문들 (점진적 노출) -->
        {#each survey.questions as q, i (q.id)}
          {#if shouldShowQuestion(q) && i <= revealUntil}
            <section class="ios-section {q.conditional_on ? 'ios-section--conditional' : ''}"
                     in:slide={{ duration: 360, easing: cubicOut }}>
              <h2 class="ios-section-title">{q.label}</h2>
              {#if q.description}
                <p class="ios-section-desc">{q.description}</p>
              {/if}

              {#if q.type === 'single' && q.options}
                <div class="ios-group">
                  {#each q.options as opt}
                    <label class="ios-row ios-row--choice">
                      <span class="ios-choice-label">{opt.label}</span>
                      <input type="radio" name={q.id}
                             checked={answers[q.id] === opt.value}
                             onchange={() => answers[q.id] = opt.value}
                             class="ios-radio" />
                    </label>
                  {/each}
                </div>
              {:else if q.type === 'multi' && q.options}
                <div class="ios-group">
                  {#each q.options as opt}
                    <label class="ios-row ios-row--choice">
                      <span class="ios-choice-label">{opt.label}</span>
                      <input type="checkbox"
                             checked={Array.isArray(answers[q.id]) && (answers[q.id] as string[]).includes(opt.value)}
                             onchange={() => toggleMulti(q.id, opt.value)}
                             class="ios-check" />
                    </label>
                  {/each}
                </div>
              {:else if q.type === 'matrix' && q.rows && q.cols && q.row_keys && q.col_keys}
                <div class="ios-matrix-wrap">
                  <table class="ios-matrix">
                    <thead>
                      <tr>
                        <th class="ios-matrix-corner"></th>
                        {#each q.cols as col}
                          <th class="ios-matrix-col-head">{col}</th>
                        {/each}
                      </tr>
                    </thead>
                    <tbody>
                      {#each q.rows as row, ri}
                        <tr class="ios-matrix-row">
                          <td class="ios-matrix-row-label">{row}</td>
                          {#each q.col_keys as colKey, ci}
                            <td class="ios-matrix-cell">
                              <label class="ios-matrix-cell-label">
                                <input
                                  type="checkbox"
                                  checked={isMatrixChecked(q.id, q.row_keys![ri], colKey)}
                                  onchange={() => toggleMulti(q.id, `${q.row_keys![ri]}_${colKey}`)}
                                  class="ios-check ios-check--matrix"
                                />
                              </label>
                            </td>
                          {/each}
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {:else if q.type === 'text'}
                <div class="ios-group">
                  <textarea bind:value={answers[q.id]}
                            placeholder={q.placeholder ?? ''}
                            class="ios-textarea" rows="3"></textarea>
                </div>
              {/if}
            </section>
          {/if}
        {/each}

        {#if revealUntil >= survey.questions.length - 1}
          <button type="submit" class="ios-submit"
                  in:fade={{ duration: 300 }}>제출하기</button>
        {:else}
          <p class="ios-progress-hint" in:fade={{ duration: 200 }}>
            답변하시면 다음 질문이 이어서 나타납니다 ↓
          </p>
        {/if}
      </form>
    {/if}
  </main>
</div>

<style>
  /* ──────── iOS 26 Liquid Glass Tokens ──────── */
  .ios-shell {
    --ios-bg-base: #f2f2f7;
    --ios-bg-grouped: #f2f2f7;
    --ios-surface: rgba(255, 255, 255, 0.72);
    --ios-surface-solid: #ffffff;
    --ios-surface-elev: rgba(255, 255, 255, 0.88);
    --ios-separator: rgba(60, 60, 67, 0.14);
    --ios-label: #000000;
    --ios-label-secondary: rgba(60, 60, 67, 0.6);
    --ios-label-tertiary: rgba(60, 60, 67, 0.3);
    --ios-fill-tertiary: rgba(118, 118, 128, 0.12);
    --ios-blue: #007aff;
    --ios-blue-pressed: #0062cc;
    --ios-radius-lg: 18px;
    --ios-radius-md: 12px;
    --ios-radius-sm: 10px;
    --ios-radius-input: 10px;
    --ios-font: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display",
                "Pretendard Variable", Pretendard, system-ui, sans-serif;

    min-height: 100vh;
    background:
      radial-gradient(1200px 600px at 10% -10%, rgba(0, 122, 255, 0.12), transparent 60%),
      radial-gradient(900px 500px at 110% 20%, rgba(175, 82, 222, 0.10), transparent 60%),
      var(--ios-bg-base);
    font-family: var(--ios-font);
    color: var(--ios-label);
    letter-spacing: -0.01em;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }

  @media (prefers-color-scheme: dark) {
    .ios-shell {
      --ios-bg-base: #000000;
      --ios-surface: rgba(28, 28, 30, 0.72);
      --ios-surface-solid: #1c1c1e;
      --ios-surface-elev: rgba(44, 44, 46, 0.88);
      --ios-separator: rgba(84, 84, 88, 0.36);
      --ios-label: #ffffff;
      --ios-label-secondary: rgba(235, 235, 245, 0.6);
      --ios-label-tertiary: rgba(235, 235, 245, 0.3);
      --ios-fill-tertiary: rgba(118, 118, 128, 0.24);
    }
  }

  /* ──────── Layout ──────── */
  .ios-container {
    max-width: 560px;
    margin: 0 auto;
    padding: 48px 20px 80px;
  }

  .ios-header {
    padding: 8px 4px 20px;
  }

  .ios-slogan {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ios-blue);
    margin-bottom: 8px;
  }

  .ios-title {
    font-size: 34px;
    font-weight: 700;
    letter-spacing: -0.024em;
    line-height: 1.15;
  }

  .ios-subtitle {
    margin-top: 6px;
    font-size: 15px;
    color: var(--ios-label-secondary);
    line-height: 1.4;
  }

  .ios-form {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  /* ──────── Section ──────── */
  .ios-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ios-section--conditional .ios-group,
  .ios-section--conditional .ios-matrix-wrap {
    outline: 2px solid rgba(0, 122, 255, 0.25);
    outline-offset: 0;
  }

  .ios-section-title {
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.012em;
    color: var(--ios-label);
    padding: 4px 4px 0;
    line-height: 1.3;
  }

  .ios-basic-hint {
    font-size: 13px;
    color: var(--ios-label-secondary);
    padding: 0 4px;
    line-height: 1.45;
    margin: -4px 0 4px;
  }

  .ios-section-desc {
    font-size: 14px;
    color: var(--ios-label-secondary);
    padding: 0 4px;
    line-height: 1.45;
    margin-top: -6px;
  }

  .ios-group {
    background: var(--ios-surface);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border-radius: var(--ios-radius-lg);
    border: 0.5px solid var(--ios-separator);
    overflow: hidden;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.4) inset;
  }

  /* ──────── Row ──────── */
  .ios-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    min-height: 44px;
    border-top: 0.5px solid var(--ios-separator);
  }

  .ios-row:first-child { border-top: none; }

  .ios-row-label {
    font-size: 15px;
    font-weight: 400;
    color: var(--ios-label);
    min-width: 96px;
    flex-shrink: 0;
  }

  .ios-required { color: #ff3b30; font-weight: 600; }

  .ios-row--choice {
    cursor: pointer;
    justify-content: space-between;
  }

  .ios-row--choice:active { background: var(--ios-fill-tertiary); }

  .ios-choice-label {
    font-size: 15px;
    color: var(--ios-label);
    flex: 1;
    line-height: 1.35;
  }

  /* ──────── Matrix ──────── */
  .ios-matrix-wrap {
    background: var(--ios-surface);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border-radius: var(--ios-radius-lg);
    border: 0.5px solid var(--ios-separator);
    overflow: hidden;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.4) inset;
  }

  .ios-matrix {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .ios-matrix-corner {
    width: 52px;
  }

  .ios-matrix-col-head {
    font-size: 12px;
    font-weight: 600;
    color: var(--ios-label-secondary);
    text-align: center;
    padding: 12px 6px 10px;
    border-bottom: 0.5px solid var(--ios-separator);
    white-space: pre-line;
    line-height: 1.3;
    letter-spacing: -0.01em;
  }

  .ios-matrix-row {
    border-top: 0.5px solid var(--ios-separator);
  }

  .ios-matrix-row-label {
    font-size: 15px;
    font-weight: 500;
    color: var(--ios-label);
    padding: 0 0 0 16px;
    text-align: left;
  }

  .ios-matrix-cell {
    text-align: center;
    padding: 10px 0;
  }

  .ios-matrix-cell-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    cursor: pointer;
  }

  .ios-check--matrix {
    width: 24px;
    height: 24px;
    border-radius: 7px;
  }

  /* ──────── Inputs ──────── */
  .ios-input,
  .ios-textarea {
    flex: 1;
    min-width: 0;
    font-family: inherit;
    font-size: 15px;
    color: var(--ios-label);
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    caret-color: var(--ios-blue);
  }

  .ios-input::placeholder,
  .ios-textarea::placeholder { color: var(--ios-label-tertiary); }

  .ios-input--mono {
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 14px;
    letter-spacing: 0.02em;
  }

  .ios-textarea {
    display: block;
    width: 100%;
    padding: 12px 16px;
    resize: vertical;
    line-height: 1.5;
  }

  /* ──────── Radio / Checkbox ──────── */
  .ios-radio,
  .ios-check {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
    flex-shrink: 0;
    cursor: pointer;
    background: transparent;
    border: 1.5px solid var(--ios-label-tertiary);
    transition: border-color 160ms ease, background 160ms ease;
  }

  .ios-radio {
    width: 22px; height: 22px;
    border-radius: 50%;
    position: relative;
  }

  .ios-radio:checked {
    border-color: var(--ios-blue);
    background: var(--ios-blue);
    box-shadow: inset 0 0 0 3.5px var(--ios-surface-solid);
  }

  .ios-check {
    width: 22px; height: 22px;
    border-radius: 6px;
    position: relative;
  }

  .ios-check:checked {
    background: var(--ios-blue);
    border-color: var(--ios-blue);
  }

  .ios-check:checked::after {
    content: '';
    position: absolute;
    inset: 0;
    background: no-repeat center/14px 14px
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'%3E%3Cpath fill-rule='evenodd' d='M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z' clip-rule='evenodd'/%3E%3C/svg%3E");
  }

  /* ──────── Link-style button ──────── */
  .ios-link-btn {
    align-self: flex-start;
    margin-left: 16px;
    margin-top: 2px;
    background: none;
    border: none;
    padding: 8px 0;
    font-size: 15px;
    font-family: inherit;
    color: var(--ios-blue);
    cursor: pointer;
  }

  .ios-link-btn:active { opacity: 0.6; }

  /* ──────── Submit ──────── */
  .ios-submit {
    margin-top: 8px;
    width: 100%;
    padding: 16px 24px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: white;
    background: linear-gradient(180deg, #2a8cff 0%, #007aff 100%);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.25) inset,
      0 8px 24px -8px rgba(0, 122, 255, 0.55);
    cursor: pointer;
    transition: transform 80ms ease, filter 160ms ease;
  }

  .ios-submit:active {
    transform: scale(0.985);
    filter: brightness(0.94);
  }

  .ios-progress-hint {
    margin-top: 8px;
    padding: 12px;
    text-align: center;
    font-size: 13px;
    color: var(--ios-label-tertiary);
    font-weight: 500;
    letter-spacing: -0.005em;
  }

  /* ──────── Notice / Welcome card ──────── */
  .ios-card {
    padding: 20px;
    border-radius: var(--ios-radius-lg);
    background: var(--ios-surface);
    border: 0.5px solid var(--ios-separator);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    font-size: 15px;
  }

  .ios-card--welcome {
    padding: 36px 24px;
    text-align: center;
    background: linear-gradient(135deg,
      rgba(0, 122, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.72) 60%);
    border-color: rgba(0, 122, 255, 0.2);
  }

  .ios-welcome-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .ios-welcome-title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--ios-label);
    margin-bottom: 12px;
  }

  .ios-welcome-body {
    font-size: 16px;
    color: var(--ios-label-secondary);
    line-height: 1.65;
    word-break: keep-all;
    overflow-wrap: break-word;
  }

  .ios-welcome-sub {
    margin-top: 20px;
    font-size: 14px;
    color: var(--ios-blue);
    font-weight: 500;
  }
</style>
